import type { FunctionComponent } from 'react';
import { lazy, Suspense } from 'react';

import { Spinner } from '@/components/atoms/Spinner';

/**
 * Wraps a lazily loaded component in a Suspense boundary with the app loader.
 *
 * The loaded component is rendered through an inner component that takes the props as one
 * object, because React types a lazy component's props as `PropsWithRef<P>`, which TypeScript
 * cannot relate back to a generic `P`.
 */
export function SuspendedLazy<P extends object>(
    params: () => Promise<{ default: FunctionComponent<P> }>
): FunctionComponent<P> {
    const LazyComponent = lazy(() =>
        params().then(({ default: Component }) => ({
            default: function LoadedComponent({ props }: { props: P }) {
                return <Component {...props} />;
            },
        }))
    );

    return function SuspendedComponent(props: P) {
        return (
            <Suspense fallback={<Spinner size="lg" />}>
                <LazyComponent props={props} />
            </Suspense>
        );
    };
}
